"use client";

import { useEffect, useState, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { useTranslations } from "next-intl";
import { checkAppUpdate, type UpdateDecision } from "@/lib/app-update/check";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.locarora.app";

interface Props {
  children: ReactNode;
}

/**
 * 네이티브 앱 부팅 시 원격 설정을 조회해 강제 업데이트/유지보수 모드를 적용.
 *
 * - 웹은 아무 로직 없이 children 그대로 렌더
 * - 네이티브: 체크 완료 전까지 children 렌더 (스플래시 이후 자연스러운 진입)
 *   체크 결과가 force/maintenance면 전체 화면 덮어서 차단
 *   recommend는 비차단 배너 (별도 컴포넌트 권장, 현재는 force/maintenance만 차단)
 *
 * Fail-open: 체크 실패 시 children 정상 렌더 (오프라인/장애 시 앱이 갇히지 않음).
 */
export function ForceUpdateGate({ children }: Props) {
  const [decision, setDecision] = useState<UpdateDecision>({ type: "none" });
  const [isNative, setIsNative] = useState(false);

  useEffect(() => {
    // mount 후 체크 (SSR에서 Capacitor 접근 방지)
    if (!Capacitor.isNativePlatform()) return;
    setIsNative(true);

    let cancelled = false;
    checkAppUpdate().then((result) => {
      if (!cancelled) setDecision(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isNative || decision.type === "none" || decision.type === "recommend") {
    return <>{children}</>;
  }

  return <UpdateBlocker decision={decision} />;
}

function UpdateBlocker({ decision }: { decision: UpdateDecision }) {
  const t = useTranslations("common.auth.forceUpdate");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      // AppUpdate 플러그인이 등록된 새 빌드에서만 In-App Update 시도.
      // 구버전 앱 또는 플러그인 로드 실패 시 Play Store URL로 직접 이동.
      if (Capacitor.isPluginAvailable("AppUpdate")) {
        try {
          const { AppUpdate } = await import(
            "@capawesome/capacitor-app-update"
          );
          if (Capacitor.getPlatform() === "android") {
            try {
              const info = await AppUpdate.getAppUpdateInfo();
              if (info.immediateUpdateAllowed) {
                await AppUpdate.performImmediateUpdate();
                return;
              }
            } catch (err) {
              console.error("[force-update] immediate update failed:", err);
            }
          }
          await AppUpdate.openAppStore();
          return;
        } catch (err) {
          console.error("[force-update] AppUpdate plugin call failed:", err);
        }
      }

      // Fallback: 스토어 웹 URL로 이동
      window.location.href = PLAY_STORE_URL;
    } catch (err) {
      console.error("[force-update] open store failed:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  if (decision.type === "maintenance") {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-white p-6"
      >
        <div className="max-w-md text-center">
          <div className="mb-6 text-5xl">🛠️</div>
          <h1 className="mb-3 text-xl font-bold text-gray-900">
            {t("maintenanceTitle")}
          </h1>
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-gray-600">
            {decision.message || t("maintenanceDescription")}
          </p>
        </div>
      </div>
    );
  }

  // force update
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-white p-6"
    >
      <div className="max-w-md text-center">
        <div className="mb-6 text-5xl">⬆️</div>
        <h1 className="mb-3 text-xl font-bold text-gray-900">
          {t("forceTitle")}
        </h1>
        <p className="mb-8 text-sm leading-relaxed text-gray-600">
          {t("forceDescription")}
        </p>
        <button
          type="button"
          onClick={handleUpdate}
          disabled={isUpdating}
          className="h-12 w-full rounded-lg bg-orange-500 text-base font-medium text-white hover:bg-orange-600 disabled:opacity-60"
        >
          {isUpdating ? t("updating") : t("updateButton")}
        </button>
      </div>
    </div>
  );
}
